import { useAchievementTypes, useReduxState } from 'hooks';
import { Alert, EmptyIndicator, FlexContainer } from 'components/common';
import { Box, BoxRow } from 'components/common/box';
import {
  AchievementCompletionClosingApprovalHistory,
  AchievementCompletionItemsCleared,
  AchievementCompletionsCloseConversation,
  AchievementCompletionsFillTemplateConversation,
  AchievementCompletionsFillTemplateTasklist,
  AchievementCompletionsSetConfidential,
  AchievementGroupsOfUsersGroupFillTemplate
} from '.';

import type { GetAchievementsRes } from 'shared-types/responses';

interface Props {
  achievement: GetAchievementsRes['achievements'][0];
  updateAchievement: () => Promise<void>;
}

const AchievementRenderTemplate = ({
  achievement,
  updateAchievement
}: Props) => {
  const { messages, theme, user } = useReduxState();

  const { isGroupAchievement, supportClosing, templateHeader } =
    useAchievementTypes({ type: achievement?.type });

  const shouldShowCloseConversation = (
    completion: GetAchievementsRes['achievements'][0]['completions'][0]
  ): boolean => {
    if (!completion) return false;
    if (!supportClosing) return false;
    if (completion.status !== 'completed') return false;
    if (!achievement.allowClosingConversation) return false;

    return true;
  };

  const components = {
    conversation: AchievementCompletionsFillTemplateConversation,
    'group-conversation': AchievementGroupsOfUsersGroupFillTemplate,
    tasklist: AchievementCompletionsFillTemplateTasklist
  };

  const { completions, isPathAchievement, type, template } = achievement;

  const Element = components[type] as
    | typeof AchievementCompletionsFillTemplateConversation
    | typeof AchievementCompletionsFillTemplateTasklist
    | typeof AchievementGroupsOfUsersGroupFillTemplate;

  const completion = completions?.[0];

  return !completion ||
    (!achievement.isIncompleteViewableByUser &&
      completion.status !== 'completed') ? null : (
    <Box
      padding={0}
      title={
        <FlexContainer
          style={{ gap: 10, justifyContent: 'space-between', width: '100%' }}
        >
          <FlexContainer style={{ gap: 7 }}>
            <div>{templateHeader}</div>

            {completion ? (
              <div
                style={{
                  color: theme.textColorLighter,
                  fontSize: '1.5rem',
                  fontWeight: 600
                }}
              >
                {completion.canSeeCompletionItems
                  ? `(${completion.numberOfUserTemplateItems}/${template.length})`
                  : ''}
              </div>
            ) : null}
          </FlexContainer>

          {completion ? (
            <AchievementCompletionsSetConfidential
              achievementId={achievement.id}
              achievementCompletionId={completion?.id}
              disabled={
                !achievement.allowConfidential ||
                !achievement.allowConfidentialUserCanSet
              }
              isConfidential={completion?.isConfidential}
              isUserOrMember={true}
              users={completion.members ?? [user]}
              updateAchievement={updateAchievement}
            />
          ) : null}
        </FlexContainer>
      }
    >
      <div className="items">
        {completion?.completionItemsClearedDate ? (
          <AchievementCompletionItemsCleared
            date={completion.completionItemsClearedDate}
            type={achievement.type}
          />
        ) : completion?.canSeeCompletionItems || isPathAchievement ? (
          <>
            {completion.userItems.map((item, i) => {
              const completionIsAvailable =
                completion || (!isGroupAchievement && isPathAchievement)
                  ? true
                  : false;

              const completionIsBlocked =
                completion?.isClosed || completion?.isInactive;

              // this is not an admin page route, so we only care about
              // the ability to answer the item from a user perspective
              // 1. No answers: depends whether the question can be marked by a user
              // 2. Group achievement: we validate whether the user can edit specific answers in the template component
              // 3. Normal achievement: we validate whether the user answered that question themselves
              const canUserAnswer =
                !item.answers.length || isGroupAchievement
                  ? item.isAnswerableByUser
                  : item.answers?.[0]?.user?.id === user?.id;

              const canAnswer =
                !completionIsBlocked && completionIsAvailable && canUserAnswer;

              const canMarkComplete =
                !completionIsBlocked &&
                completionIsAvailable &&
                item.isMarkableByUser;

              return (
                <BoxRow key={item.id} padding="5px 0">
                  <Element
                    achievement={achievement}
                    canAnswer={canAnswer}
                    canMarkComplete={canMarkComplete}
                    completionId={completion?.id}
                    key={item.id}
                    item={item}
                    itemIndex={i + 1}
                    targetUserId={user?.id}
                    updateItems={updateAchievement}
                  />
                </BoxRow>
              );
            })}

            {shouldShowCloseConversation(completion) ? (
              <BoxRow>
                {completion?.isClosed && (
                  <Alert
                    type="warning-light"
                    text={messages.achievementConversationClosed}
                  />
                )}
                <AchievementCompletionsCloseConversation
                  achievement={achievement}
                  achievementData={completion}
                  achievementId={achievement.id}
                  completionIdentifier={
                    isGroupAchievement ? completion?.id : user?.id
                  }
                  isUserOrMember={true}
                  updateData={updateAchievement}
                />
                <AchievementCompletionClosingApprovalHistory
                  achievement={achievement}
                  achievementData={completion}
                />
              </BoxRow>
            ) : null}
          </>
        ) : completion?.isConfidential ? (
          <EmptyIndicator
            description={messages.achievementCompletionConfidentialInfo}
            icon="lock"
            title={messages.commonConfidential}
          />
        ) : null}
      </div>
    </Box>
  );
};

export default AchievementRenderTemplate;
